import React, { useMemo } from "react";

import { FormFieldDto } from "../../lib/generated/api-client";
import { Field, isReadOnlyField, isRequired } from "../../lib/schema-field";
import { useDynamicForm } from "../../lib/useDynamicForm";
import { DashboardCard } from "./DashboardCard";

interface FormCardProps {
  datasourceId: string;
  name: string;
  title?: string;
  description?: string;
  fields: FormFieldDto[];
  /** Analyse columns for this form's collection. */
  schema?: Field[];
  demoMode?: boolean;
  /** Grid placement from the dashboard. */
  className?: string;
}

const dtoName = (field: FormFieldDto): string => field.field || field.name;

/** Last-resort map when the analyser never saw this column. */
const fromDto = (field: FormFieldDto): Field =>
  ({
    name: dtoName(field),
    label: field.label,
    type: field.type,
    subType: field.subType,
    nullable: !field.isRequired,
    isEnum: field.isEnum,
    enum: field.enum,
  }) as unknown as Field;

/**
 * LLM field order, with analyse `Field` metadata (primary, default, nullable)
 * winning per name. Required writable columns the prompt omitted still join
 * so create cannot skip a NOT NULL column.
 */
const overlayFields = (chosen: FormFieldDto[], analysed: Field[]): Field[] => {
  if (!chosen.length) return analysed;
  const byName = new Map(analysed.map((field) => [field.name, field]));
  const seen = new Set<string>();
  const ordered = [...chosen]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((dto) => {
      const name = dtoName(dto);
      seen.add(name);
      return byName.get(name) ?? fromDto(dto);
    });
  for (const field of analysed) {
    if (seen.has(field.name)) continue;
    if (isReadOnlyField(field)) continue;
    if (!isRequired(field)) continue;
    ordered.push(field);
    seen.add(field.name);
  }
  return ordered;
};

export const FormCard: React.FC<FormCardProps> = ({
  datasourceId,
  name,
  title,
  description,
  fields,
  schema: analysed = [],
  demoMode,
  className,
}) => {
  const schema = useMemo(
    () => overlayFields(fields, analysed),
    [fields, analysed]
  );
  const collection = fields[0]?.collection ?? "";

  const { FormModal } = useDynamicForm({
    title: title || name,
    datasourceId,
    collection,
    schema,
    demoMode,
    variant: "inline",
    // overlayFields already ordered these: LLM choice first, then the
    // required columns it left out. Ranking would scatter that.
    preserveFieldOrder: true,
  });

  return (
    <DashboardCard
      className={className}
      title={title || name}
      description={description}
    >
      {!collection ? (
        <p className="m-0 text-xs text-od-muted">
          This form does not name a collection, so it cannot be opened.
        </p>
      ) : (
        FormModal
      )}
    </DashboardCard>
  );
};

export default FormCard;
